/*──────────────────────────────────────────────────────────────
 * src/components/PurchasersTab.jsx — Freshdesk: Purchasers admin tab
 * • Per-company maintenance of the `purchasers` table.
 * • Reads purchasers from props (loaded by parent clientDetails GET),
 *   with an optional refresh via clientDetails.php GET.
 * • CREATE/EDIT via PATCH clientDetails.php?company=...&action=upsertPurchaser
 *   ({ mode:'create'|'update', purchaser:{...} }).
 * • PurchaserID is the primary key — locked in Edit mode; Create will not
 *   overwrite an existing PurchaserID (server returns 409, UI offers Edit).
 * • Matches existing ogSupport admin-tab conventions (see TagsTab).
 *──────────────────────────────────────────────────────────────*/

import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTimes,
  faSortUp,
  faSortDown,
  faSearch,
  faFileExport,
  faRotate,
} from "@fortawesome/free-solid-svg-icons";

/* ───────────── helpers ───────────── */
const getInputClasses = (th) =>
  `w-full border px-2 py-1 text-sm rounded focus:outline-none focus:ring-2 focus:ring-blue-400 ${
    th === "dark"
      ? "bg-gray-700 text-gray-100 border-gray-600"
      : "border-gray-300"
  }`;

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

const CLIENT_DETAILS = (baseUrl) =>
  `${String(baseUrl || "https://house.ogendeavors.com")
    .replace(/\/+$/, "")
    .trim()}/api/clientDetails.php`;

// Editable fields (PurchaserID is the PK). Friendly labels for the UI.
const PURCHASER_FIELDS = [
  { key: "PurchaserID", label: "Purchaser ID", pk: true },
  { key: "PurchaserName", label: "Purchaser Name", required: true },
  { key: "Type", label: "Type" },
  { key: "DispatchEmail", label: "Dispatch Email" },
  { key: "DispatchPhone", label: "Dispatch Phone" },
  { key: "OperatorEmail", label: "Operator Email(s)" },
  { key: "Note", label: "Note" },
];

const blankPurchaser = () =>
  PURCHASER_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

const normalize = (p) => {
  const out = {};
  PURCHASER_FIELDS.forEach((f) => {
    out[f.key] = String(p?.[f.key] ?? "");
  });
  return out;
};

/* ───────────── component ───────────── */
function PurchasersTab({
  theme,
  companyName,
  role = "A",
  baseUrl = "https://house.ogendeavors.com",
  purchasers: purchasersProp,
  setPurchasers: setPurchasersProp,
}) {
  const isAdmin = ["C", "A"].includes(String(role || "").toUpperCase());

  const [internal, setInternal] = useState([]);
  const purchasers = Array.isArray(purchasersProp) ? purchasersProp : internal;
  const setPurchasers =
    typeof setPurchasersProp === "function" ? setPurchasersProp : setInternal;

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [editing, setEditing] = useState(null);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("PurchaserID");
  const [sortDirection, setSortDirection] = useState("asc");

  const isDark = theme === "dark";

  const flashToast = (msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 3500);
  };

  /* refresh from server (purchasers are also seeded from parent props) */
  const refresh = async () => {
    if (!companyName) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${CLIENT_DETAILS(baseUrl)}?company=${encodeURIComponent(companyName)}`,
        { method: "GET" },
      );
      const data = await res.json();
      const single = Array.isArray(data) ? data[0] : data;
      const arr = Array.isArray(single?.purchasers) ? single.purchasers : [];
      setPurchasers(arr);
    } catch (e) {
      console.error("Failed to load purchasers:", e);
    } finally {
      setLoading(false);
    }
  };

  // If parent supplied none and we're admin, try a one-time refresh.
  useEffect(() => {
    if (isAdmin && companyName && (!purchasersProp || !purchasersProp.length)) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, isAdmin]);

  /* modal actions */
  const openCreate = () => {
    setModalMode("create");
    setEditing(blankPurchaser());
    setFormError("");
    setShowModal(true);
  };

  const openEdit = (p) => {
    setModalMode("edit");
    setEditing(normalize(p));
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditing(null);
    setFormError("");
  };

  const onField = (e) => {
    const { name, value } = e.target;
    if (name === "PurchaserID" && modalMode === "edit") return; // PK locked
    setEditing((p) => ({ ...(p || {}), [name]: value }));
  };

  const handleSave = async () => {
    if (!editing) return;
    const pid = String(editing.PurchaserID || "").trim();
    const pname = String(editing.PurchaserName || "").trim();

    if (!pid) {
      setFormError("Purchaser ID is required.");
      return;
    }
    if (!pname) {
      setFormError("Purchaser Name is required.");
      return;
    }

    // Local guard: Create must not silently overwrite an existing PK.
    if (modalMode === "create") {
      const clash = purchasers.find(
        (p) =>
          String(p.PurchaserID || "").toLowerCase() === pid.toLowerCase(),
      );
      if (clash) {
        setFormError(
          `Purchaser ID "${clash.PurchaserID}" already exists. Close and use Edit, or choose a different ID.`,
        );
        return;
      }
    }

    const payload = {
      action: "upsertPurchaser",
      mode: modalMode === "create" ? "create" : "update",
      purchaser: PURCHASER_FIELDS.reduce(
        (acc, f) => ({ ...acc, [f.key]: String(editing[f.key] ?? "").trim() }),
        {},
      ),
    };

    setSaving(true);
    setFormError("");
    try {
      const res = await fetch(
        `${CLIENT_DETAILS(baseUrl)}?company=${encodeURIComponent(companyName)}&action=upsertPurchaser`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const out = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFormError(out?.error || "Save failed. Please try again.");
        return;
      }
      // Server returns the refreshed list; prefer it.
      if (Array.isArray(out.purchasers)) {
        setPurchasers(out.purchasers);
      } else {
        await refresh();
      }
      flashToast(
        `Purchaser "${pid}" ${modalMode === "create" ? "created" : "saved"}.`,
      );
      setShowModal(false);
      setEditing(null);
    } catch (e) {
      console.error("Save purchaser failed:", e);
      setFormError("Network error while saving. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /* search / sort */
  const handleSort = (key) =>
    sortBy === key
      ? setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
      : (setSortBy(key), setSortDirection("asc"));

  const filtered = useMemo(() => {
    const term = String(searchTerm || "")
      .toLowerCase()
      .trim();
    const base = Array.isArray(purchasers) ? purchasers : [];
    const result = !term
      ? base
      : base.filter((p) =>
          PURCHASER_FIELDS.some((f) =>
            String(p?.[f.key] ?? "")
              .toLowerCase()
              .includes(term),
          ),
        );
    return [...result].sort((a, b) => {
      const res = collator.compare(
        String(a?.[sortBy] ?? ""),
        String(b?.[sortBy] ?? ""),
      );
      return sortDirection === "asc" ? res : -res;
    });
  }, [purchasers, searchTerm, sortBy, sortDirection]);

  const handleExport = () => {
    if (!filtered.length) return;
    const hdr = PURCHASER_FIELDS.map((f) => f.key);
    const rows = filtered.map((p) => hdr.map((k) => p[k] ?? ""));
    const csv = [hdr, ...rows]
      .map((r) =>
        r.map((f) => `"${String(f ?? "").replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `purchasers_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!isAdmin) {
    return (
      <div className="p-2">
        <div
          className={`rounded p-3 text-sm ${
            isDark
              ? "bg-gray-800 text-gray-200 border border-gray-700"
              : "bg-gray-50 text-gray-700 border border-gray-200"
          }`}
        >
          You do not have permission to manage purchasers.
        </div>
      </div>
    );
  }

  const TABLE_COLS = [
    "PurchaserID",
    "PurchaserName",
    "Type",
    "DispatchEmail",
    "DispatchPhone",
  ];

  return (
    <div className="p-2">
      {/* Header */}
      <div className="mb-2">
        <h3 className="text-lg font-bold">Purchasers</h3>
        <p
          className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
        >
          Maintain purchaser values used by reports/imports.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-2">
        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className={`absolute left-3 top-2 ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}
          />
          <input
            type="text"
            placeholder="Search purchasers…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`pl-9 pr-3 py-1 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isDark
                ? "bg-gray-700 text-gray-100 border border-gray-600"
                : "bg-white text-gray-700 border border-gray-300"
            }`}
          />
        </div>

        <div className="flex gap-2 flex-wrap items-center">
          <button
            onClick={openCreate}
            className="flex items-center font-bold py-1 px-3 rounded-md shadow-sm bg-blue-500 text-white hover:bg-blue-600"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Purchaser
          </button>
          <button
            onClick={handleExport}
            className="flex items-center font-bold py-1 px-3 rounded-md shadow-sm bg-green-500 text-white hover:bg-green-600"
          >
            <FontAwesomeIcon icon={faFileExport} className="mr-2" />
            Export
          </button>
          <button
            onClick={refresh}
            className={`flex items-center font-bold py-1 px-3 rounded-md shadow-sm ${
              isDark
                ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                : "bg-gray-200 hover:bg-gray-300 text-gray-800"
            }`}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faRotate} className="mr-2" />
            Refresh
          </button>
          {loading && (
            <span
              className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}
            >
              Loading…
            </span>
          )}
        </div>
      </div>

      {toast && (
        <div className="mb-2 rounded border border-green-500 bg-green-500/10 px-3 py-1 text-sm text-green-700 dark:text-green-400">
          {toast}
        </div>
      )}

      {/* Table */}
      {filtered && filtered.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead
              className={`border-b ${
                isDark
                  ? "bg-gray-700 text-gray-100 border-gray-600"
                  : "bg-gray-100 text-gray-800 border-gray-200"
              }`}
            >
              <tr>
                {TABLE_COLS.map((col) => (
                  <SortableHeader
                    key={col}
                    label={
                      PURCHASER_FIELDS.find((f) => f.key === col)?.label || col
                    }
                    columnKey={col}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    theme={theme}
                  />
                ))}
                <th className="py-1 px-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.PurchaserID}
                  className={`border-b ${
                    isDark
                      ? "border-gray-600 hover:bg-gray-600"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <td className="py-1 px-2 font-mono">{p.PurchaserID}</td>
                  <td className="py-1 px-2">{p.PurchaserName}</td>
                  <td className="py-1 px-2">{p.Type}</td>
                  <td className="py-1 px-2">{p.DispatchEmail}</td>
                  <td className="py-1 px-2">{p.DispatchPhone}</td>
                  <td className="py-1 px-2">
                    <button
                      onClick={() => openEdit(p)}
                      className="inline-flex items-center px-2 py-1 rounded bg-green-500 hover:bg-green-600 text-white text-xs"
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-1" />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-500 dark:text-gray-300">
          {loading ? "Loading purchasers…" : "No purchasers found."}
        </p>
      )}

      {/* Create/Edit Modal */}
      {showModal && editing && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-40 ${
            isDark ? "bg-black bg-opacity-80" : "bg-black bg-opacity-50"
          }`}
        >
          <div
            className={`w-full max-w-lg p-4 rounded-lg shadow-2xl relative ${
              isDark ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"
            } max-h-[85vh] overflow-y-auto`}
          >
            <div className="flex justify-between items-center mb-2 sticky top-0 bg-inherit">
              <h4 className="text-base font-bold">
                {modalMode === "edit" ? "Edit Purchaser" : "Add Purchaser"}
              </h4>
              <button
                onClick={closeModal}
                className="p-1 rounded-full text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
                aria-label="Close"
                disabled={saving}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-2">
              {PURCHASER_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block text-sm font-semibold mb-0.5">
                    {f.label}
                    {f.required ? <span className="text-red-500"> *</span> : null}
                  </label>
                  <input
                    name={f.key}
                    value={editing[f.key] ?? ""}
                    onChange={onField}
                    disabled={(f.pk && modalMode === "edit") || saving}
                    className={`${getInputClasses(theme)} ${
                      f.pk && modalMode === "edit"
                        ? "opacity-70 cursor-not-allowed"
                        : ""
                    }`}
                  />
                  {f.pk && modalMode === "edit" && (
                    <div className="text-[11px] text-gray-500 mt-1">
                      Purchaser ID is locked while editing.
                    </div>
                  )}
                </div>
              ))}
            </div>

            {formError && (
              <div className="mt-2 rounded border border-red-500 bg-red-500/10 px-3 py-1 text-sm text-red-600 dark:text-red-400">
                {formError}
              </div>
            )}

            <div className="mt-3 flex justify-end space-x-2">
              <button
                onClick={closeModal}
                disabled={saving}
                className={`px-3 py-1 rounded text-sm ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-1 rounded text-sm bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-70"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────── SortableHeader ───────── */
function SortableHeader({
  label,
  columnKey,
  sortBy,
  sortDirection,
  onSort,
  theme,
}) {
  const active = sortBy === columnKey;
  return (
    <th
      onClick={() => onSort(columnKey)}
      className="py-1 px-2 text-left cursor-pointer select-none"
    >
      <span className="flex items-center space-x-1">
        <span>{label}</span>
        {active && (
          <FontAwesomeIcon
            icon={sortDirection === "asc" ? faSortUp : faSortDown}
            className={theme === "dark" ? "text-gray-200" : "text-gray-600"}
          />
        )}
      </span>
    </th>
  );
}

export { PurchasersTab };
export default PurchasersTab;
